/**
 * Patient Registration Form Component (Step 1)
 *
 * Handles the initial registration step with email, password, and name fields.
 * Includes real-time validation, error handling, and accessibility features.
 * Integrates with existing UI components and theme system.
 * Based on the existing DieticianRegistrationForm pattern.
 *
 * @module components/patient-registration/PatientRegistrationForm
 */

import React, { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { useTheme } from '@/hooks/useTheme';
import { usePatientRegistrationFormState } from '@/hooks/usePatientRegistrationFormState';
import { cn } from '@/utils/cn';
import type { PatientRegistrationFormData } from '@/types/patientRegistration';

export interface PatientRegistrationFormProps {
  onSubmit: (data: PatientRegistrationFormData) => Promise<void>;
  initialData?: Partial<PatientRegistrationFormData>;
  isLoading: boolean;
  errors: Record<string, string>;
}

export function PatientRegistrationForm(props: PatientRegistrationFormProps) {
  const { isDark } = useTheme();

  // Use the custom hook for form state and logic
  const {
    formData,
    fieldErrors,
    fieldRefs,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit,
  } = usePatientRegistrationFormState({
    initialData: props.initialData,
    onSubmit: props.onSubmit,
  });

  // Focus password field when referral data exists
  useEffect(() => {
    const referralData = sessionStorage.getItem('referralData');
    if (referralData && fieldRefs.current.password) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        fieldRefs.current.password?.focus();
      }, 100);
    }
  }, [fieldRefs]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" role="form">
      {/* Form error display */}
      {props.errors.form && (
        <div className={cn(
          'p-4 rounded-lg border',
          isDark
            ? 'bg-red-900/20 border-red-400 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
        )} data-testid="form-error">
          <p className="text-sm font-medium">{props.errors.form}</p>
        </div>
      )}

      {/* Name fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="First Name"
          required
          error={fieldErrors.firstName || props.errors.firstName}
        >
          <Input
            ref={(el) => { fieldRefs.current.firstName = el; }}
            type="text"
            value={formData.firstName}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            onBlur={() => handleFieldBlur('firstName')}
            placeholder="Enter your first name"
            disabled={props.isLoading}
            autoComplete="given-name"
            aria-required="true"
          />
        </FormField>

        <FormField
          label="Last Name"
          required
          error={fieldErrors.lastName || props.errors.lastName}
        >
          <Input
            ref={(el) => { fieldRefs.current.lastName = el; }}
            type="text"
            value={formData.lastName}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            onBlur={() => handleFieldBlur('lastName')}
            placeholder="Enter your last name"
            disabled={props.isLoading}
            autoComplete="family-name"
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Email field */}
      <FormField
        label="Email Address"
        required
        error={fieldErrors.email || props.errors.email}
        helperText="We'll use this email for important account notifications"
      >
        <Input
          ref={(el) => { fieldRefs.current.email = el; }}
          type="email"
          value={formData.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          onBlur={() => handleFieldBlur('email')}
          placeholder="Enter your email address"
          disabled={props.isLoading}
          autoComplete="email"
          aria-required="true"
        />
      </FormField>

      {/* Password Fields Row */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Password Field */}
          <FormField
            label="Password"
            required
            error={fieldErrors.password || props.errors.password}
          >
            <Input
              ref={(el) => { fieldRefs.current.password = el; }}
              type="password"
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              onBlur={() => handleFieldBlur('password')}
              placeholder="Create a strong password"
              disabled={props.isLoading}
              autoComplete="new-password"
              aria-required="true"
            />
          </FormField>

          {/* Confirm Password Field */}
          <FormField
            label="Confirm Password"
            required
            error={fieldErrors.confirmPassword || props.errors.confirmPassword}
          >
            <Input
              ref={(el) => { fieldRefs.current.confirmPassword = el; }}
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
              onBlur={() => handleFieldBlur('confirmPassword')}
              placeholder="Confirm your password"
              disabled={props.isLoading}
              autoComplete="new-password"
              aria-required="true"
            />
          </FormField>
        </div>
        {/* Password Helper Text */}
        <p className={cn(
          "text-sm",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Must be at least 8 characters with uppercase, lowercase, and numbers
        </p>
      </div>

      {/* Submit button */}
      <Button
        type="submit"
        className="w-full"
        disabled={props.isLoading}
        loading={props.isLoading}
      >
        {props.isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>

      {/* Terms and Privacy */}
      <p className={cn(
        "text-xs text-center",
        isDark ? "text-secondary-400" : "text-secondary-600"
      )}>
        By creating an account, you agree to our{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Terms of Service (opens in new window)"
          className={cn(
            "hover:underline inline-flex items-center gap-1",
            isDark ? "text-primary-400" : "text-primary-600"
          )}
        >
          Terms of Service
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>{' '}
        and{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Privacy Policy (opens in new window)"
          className={cn(
            "hover:underline inline-flex items-center gap-1",
            isDark ? "text-primary-400" : "text-primary-600"
          )}
        >
          Privacy Policy
          <svg
            className="w-3 h-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </p>
    </form>
  );
}